import {MatDialogConfig} from '@angular/material/dialog';

/**
 * Build a `MatDialogConfig` that KEEPS the app-wide modern dialog skin.
 *
 * ## Why this exists
 *
 * `app.module.ts` provides
 * `MAT_DIALOG_DEFAULT_OPTIONS = { panelClass: 'modern-dialog', … }`,
 * and `src/dialogs.css` hangs ~300 lines of styling off that one class:
 * rounded corners, the 28px/32px body padding, the banded title, the
 * pill buttons, the `.btn-sec` action row, the inline message bars.
 *
 * Material merges the default options with the caller's config by
 * spreading — `{...defaultOptions, ...config}`. `MatDialogConfig`'s
 * constructor assigns `this.panelClass = ''`, so **every call site that
 * does `new MatDialogConfig()` silently overwrites the default with an
 * empty string** and loses the entire skin. Call sites that pass an
 * object literal (`this.dialog.open(X, { width: '350px', data })`) keep
 * it, because a literal has no `panelClass` key at all.
 *
 * That is the whole explanation for why some dialogs in this app look
 * finished and others — Create Project, Create Folder, Move Project,
 * Rename Folder, Reassign Draft/Book — render as an unpadded white box
 * with a cramped title. It was never a per-dialog styling problem; they
 * were all opted out of the skin by accident.
 *
 * ## Usage
 *
 * ```ts
 * const config = modernDialogConfig({ width: '640px' });
 * config.data = { … };
 * this.dialog.open(MoveProjectComponent, config);
 * ```
 *
 * `variant` maps onto the two modifier classes `dialogs.css` already
 * defines: `compact` tightens the padding for confirm-sized dialogs,
 * `wide` raises the max height for dialogs that manage their own
 * internal scrolling (Organize Projects, Change Packages).
 */
export function modernDialogConfig(
  overrides: Partial<MatDialogConfig> = {},
  variant: 'default' | 'compact' | 'wide' = 'default'
): MatDialogConfig {
  const config = new MatDialogConfig();
  config.disableClose = false;
  config.autoFocus = true;

  Object.assign(config, overrides);

  // Applied AFTER the overrides so a caller passing an unrelated
  // `panelClass` cannot accidentally drop the skin again — that is the
  // exact failure this helper exists to prevent.
  const panelClasses = ['modern-dialog'];
  if ( variant === 'compact' ) { panelClasses.push('modern-dialog-compact'); }
  if ( variant === 'wide' ) { panelClasses.push('modern-dialog-wide'); }
  if ( overrides && overrides.panelClass ) {
    const extra = Array.isArray(overrides.panelClass) ? overrides.panelClass : [overrides.panelClass];
    for ( const cls of extra ) {
      if ( cls && panelClasses.indexOf(cls) === -1 ) { panelClasses.push(cls); }
    }
  }
  config.panelClass = panelClasses;

  return config;
}
